import { dictionaries } from "@/dictionaries/content";

import {
  compareSchoolInternalIdAsc,
  dedupeBoundarySchoolLinks,
} from "./boundary-school-links.util";
import { formatMergedSchoolNames } from "./format-merged-school-names";

import type {
  BoundaryDisplayNameLinkSource,
  IBoundarySchoolLink,
  ResolveBoundarySchoolsDisplayNameOptions,
} from "../types/boundary-school-link.types";

export type {
  BoundaryDisplayNameLinkSource,
  ResolveBoundarySchoolsDisplayNameOptions,
} from "../types/boundary-school-link.types";

function defaultUnassignedBoundaryLabel(): string {
  return dictionaries.mapPage?.boundaryPanel?.unassignedBoundary ?? "Unassigned Boundary";
}

/**
 * Prefer full `schools[]`; otherwise synthesize a single link from primary identity fields.
 */
export function linksForBoundaryDisplayName(
  source: BoundaryDisplayNameLinkSource,
): IBoundarySchoolLink[] {
  if (source.schools && source.schools.length > 0) {
    return [...source.schools];
  }

  const schoolInternalId = source.schoolInternalId?.trim() ?? "";
  const schoolName = source.schoolName?.trim() ?? "";
  if (!schoolInternalId && !schoolName) {
    return [];
  }

  return [
    {
      schoolId: source.schoolId ?? null,
      schoolInternalId: schoolInternalId || schoolName,
      schoolName: source.schoolName,
    },
  ];
}

/**
 * Display label for a boundary from its linked schools (slash-merge with common suffix).
 * Order is schoolInternalId ASC. Color/primary identity is unchanged elsewhere.
 */
export function resolveBoundarySchoolsDisplayName(
  links: readonly IBoundarySchoolLink[] | null | undefined,
  options: ResolveBoundarySchoolsDisplayNameOptions = {},
): string {
  const unassignedLabel = options.unassignedLabel ?? defaultUnassignedBoundaryLabel();
  const sorted = dedupeBoundarySchoolLinks(links ?? []).sort((left, right) =>
    compareSchoolInternalIdAsc(left.schoolInternalId, right.schoolInternalId),
  );

  const names = sorted
    .map((link) => link.schoolName?.trim() ?? "")
    .filter((name) => name.length > 0);

  const merged = formatMergedSchoolNames(names);
  if (merged) {
    return merged;
  }

  const primaryInternalId = sorted[0]?.schoolInternalId?.trim() ?? "";
  if (primaryInternalId) {
    return primaryInternalId;
  }

  const fallbackInternalId = options.fallbackInternalId?.trim() ?? "";
  if (fallbackInternalId) {
    return fallbackInternalId;
  }

  return unassignedLabel;
}
